import api from './api';
import createCrudService from './crudService';

export const accountCodeService = createCrudService('/account-codes');
export const currencyService = createCrudService('/currencies');
export const nationalityService = createCrudService('/nationalities');
export const expenseTypeService = createCrudService('/expense-types');
export const incomeTypeService = createCrudService('/income-types');

// Customer: standard CRUD + photo upload.
export const customerService = {
  ...createCrudService('/customers'),
  uploadPhoto: (id, file) => {
    const form = new FormData();
    form.append('photo', file);
    return api
      .post(`/customers/${id}/photo`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
  removePhoto: (id) => api.delete(`/customers/${id}/photo`).then((r) => r.data),
  block: (id) => api.patch(`/customers/${id}/block`).then((r) => r.data),
  unblock: (id) => api.patch(`/customers/${id}/unblock`).then((r) => r.data),
};

// Bank account: standard CRUD + block/unblock.
export const bankAccountService = {
  ...createCrudService('/bank-accounts'),
  block: (id) => api.patch(`/bank-accounts/${id}/block`).then((r) => r.data),
  unblock: (id) => api.patch(`/bank-accounts/${id}/unblock`).then((r) => r.data),
};

// Document series (Settings).
export const documentSeriesService = {
  list: () => api.get('/document-series').then((r) => r.data),
  get: (id) => api.get(`/document-series/${id}`).then((r) => r.data),
  create: (payload) => api.post('/document-series', payload).then((r) => r.data),
  update: (id, payload) => api.put(`/document-series/${id}`, payload).then((r) => r.data),
};

export const authService = {
  login: (username, password) =>
    api.post('/auth/login', { username, password }).then((r) => r.data),
  me: () => api.get('/auth/me').then((r) => r.data),
  changePassword: (currentPassword, newPassword) =>
    api.patch('/auth/change-password', { currentPassword, newPassword }).then((r) => r.data),
};

// --- Settings area (Phase 1b) ---
export const settingService = {
  get: () => api.get('/settings/general').then((r) => r.data),
  save: (payload) => api.put('/settings/general', payload).then((r) => r.data),
};

export const userService = {
  list: () => api.get('/users').then((r) => r.data),
  create: (payload) => api.post('/users', payload).then((r) => r.data),
  update: (id, payload) => api.put(`/users/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/users/${id}`).then((r) => r.data),
  block: (id) => api.patch(`/users/${id}/block`).then((r) => r.data),
  unblock: (id) => api.patch(`/users/${id}/unblock`).then((r) => r.data),
};

export const userGroupService = {
  list: () => api.get('/user-groups').then((r) => r.data),
  get: (id) => api.get(`/user-groups/${id}`).then((r) => r.data),
  create: (payload) => api.post('/user-groups', payload).then((r) => r.data),
  update: (id, payload) => api.put(`/user-groups/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/user-groups/${id}`).then((r) => r.data),
  getPermissions: (id) => api.get(`/user-groups/${id}/permissions`).then((r) => r.data),
  savePermissions: (id, rows) =>
    api.put(`/user-groups/${id}/permissions`, { rows }).then((r) => r.data),
};

export const formService = {
  list: () => api.get('/forms').then((r) => r.data),
};

export const uploadService = {
  parse: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api
      .post('/transactions/upload/parse', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
  save: (file, idempotencyKey) => {
    const form = new FormData();
    form.append('file', file);
    form.append('idempotency_key', idempotencyKey);
    return api
      .post('/transactions/upload/save', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
};

export const bulkDeleteService = {
  execute: (dateFrom, dateTo) =>
    api.post('/bulk-delete/execute', { dateFrom, dateTo }).then((r) => r.data),
};

export const openPeriodService = {
  get: () => api.get('/settings/open-period').then((r) => r.data),
  save: (payload) => api.put('/settings/open-period', payload).then((r) => r.data),
};

export const buySellService = {
  list: (dateFrom, dateTo) => {
    const params = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    return api.get('/transactions/buy-sell', { params }).then((r) => r.data);
  },
  get: (id) => api.get(`/transactions/buy-sell/${id}`).then((r) => r.data),
  create: (payload) => api.post('/transactions/buy-sell', payload).then((r) => r.data),
  update: (id, payload) => api.put(`/transactions/buy-sell/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/transactions/buy-sell/${id}`).then((r) => r.data),
};

export const transferService = {
  list: (dateFrom, dateTo) => {
    const params = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    return api.get('/transactions/transfer', { params }).then((r) => r.data);
  },
  get: (id) => api.get(`/transactions/transfer/${id}`).then((r) => r.data),
  create: (payload) => api.post('/transactions/transfer', payload).then((r) => r.data),
  update: (id, payload) => api.put(`/transactions/transfer/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/transactions/transfer/${id}`).then((r) => r.data),
};

export const expenseService = {
  list: (dateFrom, dateTo) => {
    const params = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    return api.get('/transactions/expense', { params }).then((r) => r.data);
  },
  get: (id) => api.get(`/transactions/expense/${id}`).then((r) => r.data),
  create: (payload) => api.post('/transactions/expense', payload).then((r) => r.data),
  update: (id, payload) => api.put(`/transactions/expense/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/transactions/expense/${id}`).then((r) => r.data),
  uploadPhoto: (id, file) => {
    const form = new FormData();
    form.append('photo', file);
    return api
      .post(`/transactions/expense/${id}/photo`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
  removePhoto: (id) => api.delete(`/transactions/expense/${id}/photo`).then((r) => r.data),
};

export const incomeService = {
  list: (dateFrom, dateTo) => {
    const params = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    return api.get('/transactions/income', { params }).then((r) => r.data);
  },
  get: (id) => api.get(`/transactions/income/${id}`).then((r) => r.data),
  create: (payload) => api.post('/transactions/income', payload).then((r) => r.data),
  update: (id, payload) => api.put(`/transactions/income/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/transactions/income/${id}`).then((r) => r.data),
};

function buildParams({ dateFrom, dateTo, ...rest }) {
  const params = { date_from: dateFrom, date_to: dateTo };
  Object.entries(rest).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) params[k] = v;
  });
  return params;
}

function downloadExport(path, params, fallbackName) {
  return api
    .get(path, { params, responseType: 'blob' })
    .then((r) => {
      const disposition = r.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : fallbackName;
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    });
}

export const buySellReportService = {
  list: (filters) => api.get('/reports/buy-sell', { params: buildParams(filters) }).then((r) => r.data),
  export: (filters) => downloadExport('/reports/buy-sell/export', buildParams(filters), 'buy-sell-report.xlsx'),
};

export const bankReportService = {
  list: (filters) => api.get('/reports/bank', { params: buildParams(filters) }).then((r) => r.data),
  export: (filters) => downloadExport('/reports/bank/export', buildParams(filters), 'bank-report.xlsx'),
  allBalances: (filters) => api.get('/reports/bank/all', { params: buildParams(filters) }).then((r) => r.data),
  exportAllBalances: (filters) =>
    downloadExport('/reports/bank/all/export', buildParams(filters), 'bank-balances.xlsx'),
};

export const expenseIncomeReportService = {
  list: (filters) => api.get('/reports/expense-income', { params: buildParams(filters) }).then((r) => r.data),
  export: (filters) =>
    downloadExport('/reports/expense-income/export', buildParams(filters), 'expense-income-report.xlsx'),
};

export const profitLossReportService = {
  list: (filters) => api.get('/reports/profit-loss', { params: buildParams(filters) }).then((r) => r.data),
  export: (filters) => downloadExport('/reports/profit-loss/export', buildParams(filters), 'profit-loss-report.xlsx'),
};

export const currencyBalanceReportService = {
  list: (filters) => api.get('/reports/currency-balance', { params: buildParams(filters) }).then((r) => r.data),
  export: (filters) =>
    downloadExport('/reports/currency-balance/export', buildParams(filters), 'currency-balance-report.xlsx'),
};

export const uploadDocumentService = {
  list: (dateFrom, dateTo) =>
    api.get('/transactions/upload-document', { params: { dateFrom, dateTo } }).then((r) => r.data),
  create: (payload, file) => {
    const form = new FormData();
    Object.entries(payload).forEach(([k, v]) => form.append(k, v));
    form.append('file', file);
    return api
      .post('/transactions/upload-document', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
  remove: (id) => api.delete(`/transactions/upload-document/${id}`).then((r) => r.data),
  download: (id, fallbackName) => downloadExport(`/transactions/upload-document/${id}/file`, undefined, fallbackName),
};

export const monthlyClosingService = {
  preview: (period) => api.post('/monthly-closing/preview', { period }).then((r) => r.data),
  close: (period) => api.post('/monthly-closing/close', { period }).then((r) => r.data),
  get: (period) => api.get(`/monthly-closing/${period}`).then((r) => r.data),
};

// Builds an image URL for a photo stored in the DB (see cCustomerImage /
// cTrxBiayaImage) — served via routes/photos.js, not a static file.
// entity: 'customer' | 'expense'; variant: 'photo' (full size) | 'thumb'.
// Frontend must always use this — never construct the URL manually.
export function getPhotoUrl(entity, id, variant = 'photo') {
  if (!id) return null;
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/api\/v1\/?$/, '');
  return `${base}/uploads/${entity}/${id}/${variant}`;
}
